import { AssociationService } from "@/service/AssociationService";
import { ASSOCIATION_CONSTANTS } from "@/util/constants";
import { getModelDisplayName } from "@/util/customObjectUtils";

/**
 * Raw association item as returned by the associations API.
 */
export interface AssociationApiItem {
  id: string;
  key?: string;
  firstObjectLabel?: string;
  firstObjectKey?: string;
  secondObjectKey?: string;
  firstObjectToSecondObjectCardinality?: string;
}

/**
 * A single association option for a dropdown/select. This is the one canonical
 * option shape written to `store.associationCache`, so every producer (the
 * dialog, the opportunity modal composable, and the load-time validator) yields
 * identical labels regardless of which one fills the cache first.
 */
export interface AssociationPickerOption {
  label: string;
  value: string;
  description: string;
  firstObjectKey?: string;
  secondObjectKey?: string;
}

/** Persisted object-pair association settings (all fields required together). */
export interface PairAssociationSettings {
  selectedAssociation: string[];
  objectModel: string;
  withObjectModel: string;
  firstObjectKey: string;
  secondObjectKey: string;
}

/**
 * Build the persisted pair-association settings from a resolved dropdown option,
 * keeping `withObjectModel` and the two object keys atomic. Returns `undefined`
 * unless a selection resolves to an option carrying BOTH object keys — writing
 * `withObjectModel` without the keys yields a config the backend links
 * inconsistently (records created with no association and no error).
 *
 * @param objectModel - The object being configured (e.g. "opportunity")
 * @param withObjectModel - The paired object (undefined outside pair mode)
 * @param selectedId - The chosen association id (empty when none selected)
 * @param option - The resolved dropdown option for `selectedId`, if found
 */
export const buildPairAssociationSettings = (
  objectModel: string,
  withObjectModel: string | undefined,
  selectedId: string | undefined,
  option: Pick<AssociationPickerOption, "firstObjectKey" | "secondObjectKey"> | undefined
): PairAssociationSettings | undefined => {
  if (
    !withObjectModel ||
    !selectedId ||
    !option?.firstObjectKey ||
    !option?.secondObjectKey
  ) {
    return undefined;
  }
  return {
    selectedAssociation: [selectedId],
    objectModel,
    withObjectModel,
    firstObjectKey: option.firstObjectKey,
    secondObjectKey: option.secondObjectKey,
  };
};

/**
 * Generate cache key for association data
 *
 * @param objectModel - The custom object model (e.g., "custom_objects.doctors")
 * @param locationId - The location ID
 * @returns Formatted cache key string
 */
export const generateCacheKey = (objectModel?: string, locationId?: string): string => {
  return `${objectModel}_${locationId}`;
};

type ListFormatCtor = new (
  locales?: string,
  options?: { style?: string; type?: string }
) => { format(items: string[]): string };

/**
 * Join names using the given locale's list conjunction ("A and B" / "A und B" /
 * "A, B, and C"), instead of a hardcoded English " and ". vue-i18n locales use
 * underscores (e.g. "pt_BR"); Intl expects BCP-47 hyphens. Falls back to a comma
 * join when Intl.ListFormat is unavailable or the locale tag is unsupported.
 *
 * @param items - Names to join (falsy entries are dropped)
 * @param locale - Active locale (vue-i18n form, e.g. "en", "pt_BR")
 */
export const formatLocalizedList = (items: string[], locale: string): string => {
  const names = items.filter(Boolean);
  const ListFormat = (Intl as unknown as { ListFormat?: ListFormatCtor })
    .ListFormat;
  if (!ListFormat) {
    return names.join(", ");
  }
  try {
    return new ListFormat(String(locale).replace(/_/g, "-"), {
      style: "long",
      type: "conjunction",
    }).format(names);
  } catch {
    // Fall back to a plain join if the locale tag is unsupported by Intl.
    return names.join(", ");
  }
};

/** "MANY_TO_MANY" -> "Many to many" (falls back to the localized default). */
const toSentenceCardinality = (
  t: (key: string) => string,
  cardinality?: string
): string => {
  if (!cardinality) {
    return t("common.manyToMany");
  }
  const words = cardinality.toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Transform raw association API items into dropdown options. Single source of
 * truth for the option `description` string so cached options stay consistent.
 *
 * @param associations - Raw association items from the API
 * @param customObjects - Array of custom objects from the store
 * @param t - Translation function
 */
export const transformAssociations = (
  associations: AssociationApiItem[],
  customObjects: any[],
  t: (key: string) => string
): AssociationPickerOption[] => {
  return associations.map((association) => {
    const firstName = getModelDisplayName(
      association.firstObjectKey || "",
      customObjects,
      t
    );
    const secondName = getModelDisplayName(
      association.secondObjectKey || "",
      customObjects,
      t
    );
    return {
      label:
        association.firstObjectLabel ||
        association.key ||
        t("common.unknownAssociation"),
      value: association.id,
      // e.g. "Many to many · Company → Opportunity"
      description: `${toSentenceCardinality(
        t,
        association.firstObjectToSecondObjectCardinality
      )} · ${firstName} → ${secondName}`,
      firstObjectKey: association.firstObjectKey,
      secondObjectKey: association.secondObjectKey,
    };
  });
};

/**
 * Fetch associations between two objects, with a swapped-direction retry when
 * linking two non-contact objects (association directionality is not guaranteed).
 * Single source of truth for the fetch used by the dialog, the opportunity modal
 * composable, and the load-time validator.
 *
 * @param objectModel - The object being configured
 * @param targetObjectModel - The paired object (or "contact" for contact mode)
 * @param locationId - The location ID
 * @returns Raw association items (empty array on no result)
 */
export const fetchAssociationsWithSwap = async (
  objectModel: string,
  targetObjectModel: string,
  locationId: string
): Promise<AssociationApiItem[]> => {
  const isObjectPairMode =
    targetObjectModel !== ASSOCIATION_CONSTANTS.CONTACT_OBJECT_TYPE;

  let response = await AssociationService.getAssociations(
    objectModel,
    locationId,
    targetObjectModel
  );
  if (
    isObjectPairMode &&
    !(response.data?.associations && response.data.associations.length)
  ) {
    response = await AssociationService.getAssociations(
      targetObjectModel,
      locationId,
      objectModel
    );
  }
  return response.data?.associations || [];
};

/**
 * Format cardinality string from API response to human-readable format
 *
 * @param cardinality - The cardinality string from API (e.g., "MANY_TO_MANY")
 * @param fallbackText - Fallback text if cardinality is empty
 * @returns Formatted cardinality string (e.g., "Many to Many")
 *
 * Logic flow:
 * 1. Replace underscores with spaces
 * 2. Split into words
 * 3. Capitalize first letter of each word, lowercase the rest
 * 4. Join with spaces
 */
export const formatCardinality = (cardinality?: string, fallbackText = "Many to Many"): string => {
  if (!cardinality) {
    return fallbackText;
  }

  return cardinality
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Get custom object labels from store
 *
 * @param customObjects - Array of custom objects from store
 * @param objectModel - The object model to find labels for
 * @returns Object labels with plural and singular forms
 */
export const getCustomObjectLabels = (customObjects: any[], objectModel?: string) => {
  if (!objectModel) {
    return { plural: "Contacts", singular: "Contact" };
  }

  const customObject = customObjects?.find((obj) => obj.key === objectModel);
  return customObject?.labels || { plural: "Contacts", singular: "Contact" };
};

/**
 * Set default association selection based on saved settings or first available option
 *
 * @param options - Available association options
 * @param savedAssociation - Previously saved association settings
 * @param currentObjectModel - Current object model being configured
 * @returns Selected association ID or empty string
 *
 * Logic flow:
 * 1. Check if options are available
 * 2. Look for saved association matching current object model
 * 3. Verify saved association exists in current options
 * 4. Return saved association or first available option
 */
export const getDefaultAssociationSelection = (
  options: any[],
  savedAssociation: any,
  currentObjectModel?: string
): string => {
  if (!options || options.length === 0) {
    return "";
  }

  // Check if there's a saved association for this object model
  if (
    savedAssociation?.objectModel === currentObjectModel &&
    savedAssociation?.selectedAssociation
  ) {
    // Handle both string and string[] shapes for selectedAssociation
    const savedSelection = Array.isArray(savedAssociation.selectedAssociation)
      ? savedAssociation.selectedAssociation[0]
      : savedAssociation.selectedAssociation;

    if (options.some((option) => option.value === savedSelection)) {
      return savedSelection;
    }
  }

  // Default to first option if no saved association or saved association not found
  return options[0]?.value || "";
};

/**
 * Check if this is the only custom object field of a specific type
 *
 * @param elements - Form elements (for forms) or null (for surveys/quizzes)
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null (for forms)
 * @param objectModel - The object model to check
 * @returns Boolean indicating if this is the only field of this type
 */
export const isOnlyCustomObjectField = (
  elements: any[] | null,
  slides: any[] | null,
  objectModel?: string
): boolean => {
  if (!objectModel) {
    return false;
  }

  const customObjectFields = elements
    ? elements.filter((element) => element.model === objectModel)
    : slides?.flatMap(
        (slide) => slide.slideData?.filter((element) => element.model === objectModel) || []
      ) || [];

  return customObjectFields.length === 1;
};

/**
 * Check if any custom object fields of a specific type remain
 *
 * @param elements - Form elements (for forms) or null (for surveys/quizzes)
 * @param slides - Survey/quiz slides (for surveys/quizzes) or null (for forms)
 * @param objectModel - The object model to check
 * @returns Boolean indicating if any fields of this type remain
 */
export const hasCustomObjectFields = (
  elements: any[] | null,
  slides: any[] | null,
  objectModel?: string
): boolean => {
  if (!objectModel) {
    return false;
  }

  const customObjectFields = elements
    ? elements.filter((element) => element.model === objectModel)
    : slides?.flatMap(
        (slide) => slide.slideData?.filter((element) => element.model === objectModel) || []
      ) || [];

  return customObjectFields.length > 0;
};
